import { api } from '@/store/api'
import type {
  ModerationState,
  UpdateImpact,
  UpdateSubmit,
  Visibility,
  VisibilityChange,
} from './types'
import { toVisibilityChange, withRetryAfter } from './lib/reviewResponse'

export const moderationApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCourseModeration: build.query<ModerationState, number>({
      query: (id) => `gamified-courses/${id}/moderation`,
      providesTags: (_r, _e, id) => [{ type: 'Moderation', id }],
    }),
    getUpdateImpact: build.query<UpdateImpact, number>({
      query: (id) => `gamified-courses/${id}/update-impact`,
      // Без черновика бэк отдаёт пустое тело — нормализуем в нулевое влияние.
      transformResponse: (body: Partial<UpdateImpact> | null): UpdateImpact => ({
        removedLessons: body?.removedLessons ?? [],
        removedBlocks: body?.removedBlocks ?? [],
        retypedBlocks: body?.retypedBlocks ?? [],
        affectedLearners: body?.affectedLearners ?? 0,
      }),
      keepUnusedDataFor: 0,
    }),
    submitCourseUpdate: build.mutation<UpdateSubmit, number>({
      query: (id) => ({ url: `gamified-courses/${id}/submit-update`, method: 'POST' }),
      transformResponse: (body: { reviewId: number }): UpdateSubmit => ({
        kind: 'review',
        reviewId: body.reviewId,
      }),
      transformErrorResponse: withRetryAfter,
      // Тегов нет: `202` черновик не меняет (`draftId` прежний, курс перечитывать нечем), а
      // свежую модерацию приносит `refresh()` в `sendDraftUpdate`. Тег `Moderation` дал бы
      // ВТОРОЙ GET при подписанном поллере. Курс перечитает поллер на `pending → итог`.
    }),
    discardCourseDraft: build.mutation<void, number>({
      query: (id) => ({ url: `gamified-courses/${id}/draft`, method: 'DELETE' }),
      // Только `Moderation`, НЕ `Course`: оба запроса ушли бы одним проходом, и
      // `getEditorCourse` взял бы из кеша модерации ещё удалённый `draftId` (404).
      // Курс перечитывает `ModerationPoller`, когда свежая модерация принесла `draftId: null`.
      invalidatesTags: (_r, _e, id) => [{ type: 'Moderation', id }],
    }),
    setCourseVisibility: build.mutation<VisibilityChange, { id: number; visibility: Visibility }>({
      query: ({ id, visibility }) => ({
        url: `gamified-courses/${id}/visibility`,
        method: 'PATCH',
        body: { visibility },
      }),
      transformResponse: toVisibilityChange,
      transformErrorResponse: withRetryAfter,
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Moderation', id },
        { type: 'Course', id },
        { type: 'Course', id: 'LIST' },
        { type: 'Course', id: 'CATALOG' },
      ],
    }),
  }),
})

export const {
  useGetCourseModerationQuery,
  useSetCourseVisibilityMutation,
  useGetUpdateImpactQuery,
  useLazyGetUpdateImpactQuery,
  useSubmitCourseUpdateMutation,
  useDiscardCourseDraftMutation,
} = moderationApi
